import { useState } from 'react';
import { Select } from '../../../design-system';
import { TASK_STATUSES, dueAging } from '../../../lib/taskStatus';
import { clientOf, typeOf, assigneeOf, initialsOf } from '../../../lib/taskViews';

// The board. Columns are the groups; a card can be dragged to another column.
//
// ⚠️ DRAGGING ONLY MEANS ANYTHING WHEN THE COLUMNS ARE STATUSES. Grouped by
// client, dropping a card on "Rajwadi Foods" would be asking to reassign the
// task to a different client — a far bigger act than it looks, and not one a
// careless drag should perform. So the board drags only under `groupBy
// === 'status'` and is a read-only board otherwise, which the column header
// says out loud rather than leaving the gesture to fail silently.
//
// ⚠️ NATIVE HTML5 DRAG, NO LIBRARY — and it does not work on touch, which is
// not a bug that can be fixed here: `dragstart` is simply not a touch event.
// Every card therefore ALSO carries a status dropdown, which is the only path
// on a phone and a perfectly good one on a desktop. The gesture is the
// shortcut, never the sole route.

const fmtDate = (d) => (d
  ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
  : '—');

function TaskBoardView({ groups, groupBy, onOpen, onPatch, canEdit }) {
  const draggable = canEdit && groupBy === 'status';
  const [dragId, setDragId] = useState(null);
  const [overKey, setOverKey] = useState(null);

  const drop = (groupKey) => {
    setOverKey(null);
    const id = dragId;
    setDragId(null);
    if (!id || !draggable) return;
    const task = groups.flatMap((g) => g.tasks).find((t) => t._id === id);
    // Dropped back where it started — not a change, and patching it would
    // write an audit entry for a gesture that did nothing.
    if (!task || task.status === groupKey) return;
    onPatch(task, { status: groupKey });
  };

  return (
    <div className="tv-board">
      {groups.map((g) => (
        <section
          key={g.key}
          className={`tv-col${overKey === g.key ? ' tv-col-over' : ''}`}
          onDragOver={(e) => {
            if (!draggable) return;
            // ⚠️ preventDefault is what MAKES a element a drop target. Without
            // it the browser refuses the drop and the card springs back, which
            // reads as the board being broken rather than as a missing line.
            e.preventDefault();
            setOverKey(g.key);
          }}
          onDragLeave={() => setOverKey((k) => (k === g.key ? null : k))}
          onDrop={() => drop(g.key)}
        >
          <header className="tv-col-head">
            <span className="tv-col-name">{g.label}</span>
            <span className="tv-col-count">{g.tasks.length}</span>
          </header>

          <div className="tv-col-body">
            {g.tasks.map((t) => {
              const age = dueAging(t);
              const client = clientOf(t);
              const type = typeOf(t);
              const who = assigneeOf(t);
              return (
                <article
                  key={t._id}
                  className={`tv-card tv-edge-${age.tone}${dragId === t._id ? ' tv-card-dragging' : ''}`}
                  draggable={draggable}
                  onDragStart={(e) => {
                    setDragId(t._id);
                    e.dataTransfer.effectAllowed = 'move';
                    // Firefox refuses to start a drag at all without data set.
                    e.dataTransfer.setData('text/plain', t._id);
                  }}
                  onDragEnd={() => { setDragId(null); setOverKey(null); }}
                >
                  <button type="button" className="tv-card-title" onClick={() => onOpen(t)}>
                    {t.title}
                  </button>

                  <div className="tv-card-meta">
                    <span className="tv-row-client">{client?.displayName || 'Internal'}</span>
                    {type && <><span className="tv-dot" aria-hidden="true">·</span>{type.name}</>}
                  </div>

                  <div className="tv-card-foot">
                    <span className="tv-card-who">
                      <span className={`tv-avatar${who ? '' : ' tv-avatar-none'}`} aria-hidden="true">
                        {who ? initialsOf(who.name) : '–'}
                      </span>
                      <span className={`tv-due-date tv-tone-${age.tone}`}>{fmtDate(t.dueDate)}</span>
                    </span>
                    {t.priority && t.priority !== 'medium' && (
                      <span className={`tv-chip tv-chip-${t.priority}`}>{t.priority}</span>
                    )}
                  </div>

                  {/* The touch path, and the keyboard one. Not a fallback for a
                      failed drag — the only route that exists on a phone. */}
                  {canEdit && (
                    <Select
                      className="tv-card-status"
                      value={t.status}
                      aria-label={`Status of ${t.title}`}
                      onChange={(e) => onPatch(t, { status: e.target.value })}
                    >
                      {TASK_STATUSES.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </Select>
                  )}
                </article>
              );
            })}

            {g.tasks.length === 0 && (
              <p className="tv-col-empty">
                {draggable ? 'Drop a task here' : 'Nothing here'}
              </p>
            )}
          </div>
        </section>
      ))}
    </div>
  );
}

export default TaskBoardView;
